<template>
  <div class="space-y-6">
    <HermesCard title="Drafts">
      <p class="text-gray-600 mb-4">
        Work on a copy of the prompt, result schema and display without changing what users get. Saving a draft never makes a new version.
        Summarize, program buttons and schedules keep using live version v{{ template.version }} until you promote a draft.
      </p>
      <div v-if="error" role="alert" class="mb-4 rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ error }}</div>
      <ul class="divide-y divide-gray-200 mb-4">
        <li v-if="!drafts.length" class="py-3 text-gray-500">No drafts yet.</li>
        <li v-for="d in drafts" :key="d.id" class="py-3 flex items-center gap-3">
          <button type="button" :class="[TEXT_BUTTON, d.id === (current || {}).id ? 'underline' : '']" :data-test="`open-draft-${d.id}`" @click="open(d)">{{ d.name }}</button>
          <span class="text-gray-500">based on v{{ d.base_version }}<span v-if="d.created_by_name"> · <span class="sensitive" data-spoof-kind="person">{{ d.created_by_name }}</span></span> · <span :title="absolute(d.updated_at)">{{ relative(d.updated_at) }}</span></span>
          <span v-if="d.base_version !== template.version" class="rounded bg-neutral-100 px-2 text-gray-900">Live is now v{{ template.version }}</span>
        </li>
      </ul>
      <div class="flex items-end gap-3">
        <label class="block flex-1"><span class="block text-gray-500 mb-2">New draft name</span><input v-model="newName" :class="INPUT" data-test="new-draft-name"></label>
        <button type="button" :class="SECONDARY_BUTTON" :disabled="!newName.trim() || busy" data-test="create-draft" @click="create">New draft from live</button>
      </div>
    </HermesCard>

    <HermesCard v-if="current" :title="`Draft: ${current.name}`">
      <div class="space-y-4">
        <div class="flex flex-wrap items-center gap-3 rounded-md bg-gray-50 px-3 py-2" data-test="lineage">
          <span>Copied from <strong>v{{ current.base_version }}</strong><span v-if="liveMoved"> · live is now <strong>v{{ template.version }}</strong></span></span>
          <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" :disabled="!baseVersion" data-test="diff-mine" @click="showDiff('mine')">See my changes (vs v{{ current.base_version }})</button>
          <button v-if="liveMoved" type="button" :class="TEXT_BUTTON" :disabled="!baseVersion" data-test="diff-live" @click="showDiff('live')">See what changed in live (v{{ current.base_version }} → v{{ template.version }})</button>
        </div>
        <nav class="flex gap-6 border-b border-gray-200">
          <button
v-for="t in EDIT_TABS" :key="t.key" type="button" :data-test="`tab-${t.key}`"
                  :class="['py-2 font-medium border-b-2 -mb-px', editTab === t.key ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900']"
                  @click="switchTab(t.key)">{{ t.label }}</button>
        </nav>
        <HermesSectionBuilder v-if="editTab === 'document'" v-model="sections" :title="template.name" :sample="draftResult ? draftResult.data : null" />
        <div v-else-if="editTab === 'prompt'">
          <div class="mb-2 flex items-center gap-2">
            <span class="text-gray-500">The general instructions. Each section's own instructions live on the section.</span>
            <div class="ml-auto inline-flex rounded-md border border-gray-200 overflow-hidden">
              <button v-for="v in ['read', 'edit']" :key="v" type="button" :class="['px-3 py-1', promptView === v ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" :data-test="`prompt-${v}`" @click="promptView = v">{{ v === 'read' ? 'Read' : 'Edit' }}</button>
            </div>
          </div>
          <div v-if="promptView === 'read'" class="prose prose-sm max-w-none rounded-md border border-gray-200 px-4 py-3" data-test="prompt-rendered" v-html="promptHtml" />
          <textarea v-else v-model="form.prompt" rows="24" :class="[INPUT, 'font-mono']" data-test="draft-prompt" />
          <div v-if="isSuperuser && promptNotes.length" class="mt-3 rounded-md bg-neutral-50 px-3 py-2" data-test="draft-prompt-lint">
            <div class="font-medium text-gray-900 mb-1">Markdown lint · {{ promptNotes.length }} note{{ promptNotes.length === 1 ? '' : 's' }}<span class="ml-2 rounded bg-white border border-gray-200 px-2 text-[11px] font-normal text-gray-500">Beta · superusers only</span></div>
            <ul class="space-y-1">
              <li v-for="(n, i) in promptNotes" :key="i" class="text-gray-700"><span class="font-mono text-gray-500">Line {{ n.line }}</span> · {{ n.message }}</li>
            </ul>
          </div>
        </div>
        <div v-else class="space-y-4">
          <p class="text-gray-500">The raw result schema and display the sections are saved as. Edit here only for settings the Document tab doesn't cover.</p>
          <label class="block"><span class="block text-gray-500 mb-2">Result schema (JSON)</span><textarea v-model="form.schemaText" rows="12" :class="[INPUT, 'font-mono']" /></label>
          <label class="block"><span class="block text-gray-500 mb-2">Display (JSON)</span><textarea v-model="form.displayText" rows="8" :class="[INPUT, 'font-mono']" /></label>
        </div>
        <p v-if="jsonError" class="rounded bg-neutral-50 px-2 py-1 text-gray-900">{{ jsonError }}</p>
        <div class="flex items-center gap-3 border-t border-gray-200 pt-3">
          <span v-if="note" class="text-gray-600">{{ note }}</span>
          <span v-else-if="dirty" class="text-gray-600">Unsaved changes</span>
          <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" :disabled="busy" data-test="discard-draft" @click="confirmDiscard = true">Discard draft</button>
          <button type="button" :class="SECONDARY_BUTTON" :disabled="busy || !!jsonError || !dirty" data-test="save-draft" @click="save">Save draft</button>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="busy || dirty" data-test="promote-draft" @click="openPromote">Promote to live…</button>
        </div>
        <div v-if="confirmDiscard" class="rounded-md bg-neutral-50 px-4 py-3 text-gray-900 flex items-center gap-3">
          <span>Discard draft “{{ current.name }}”? Live and other drafts are not affected.</span>
          <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" @click="confirmDiscard = false">Keep it</button>
          <button type="button" :class="PRIMARY_BUTTON" data-test="confirm-discard" @click="discard">Discard</button>
        </div>
      </div>
    </HermesCard>

    <HermesCard v-if="current" title="Ask AI">
      <HermesAuthoringAssistant :draft-id="current.id" :dirty="dirty" @apply="applyProposal" />
    </HermesCard>

    <HermesCard v-if="current" title="Try it on an engagement">
      <p class="text-gray-600 mb-4">Runs the saved draft on one engagement with inputs built fresh from it. A draft run creates no documents or Work and sends nothing.</p>
      <div class="flex items-end gap-3">
        <label class="block"><span class="block text-gray-500 mb-2">Engagement (Work id)</span><input :value="workId || ''" type="text" inputmode="numeric" placeholder="e.g. 3234179" :class="SIZED_INPUT" data-test="work-id" @input="setWorkId($event)"></label>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="!workId || busy || dirty || draftLive" data-test="run-draft" @click="run">{{ draftLive ? 'Running…' : 'Run draft' }}</button>
        <span v-if="dirty" class="text-gray-500">Save the draft first.</span>
      </div>
      <p v-if="draftLive" class="mt-3 text-gray-600" data-test="running-note">Running on engagement {{ draftRun.unit_key }}… it appears below when it finishes.</p>
      <div v-if="draftRuns.length" class="mt-6 border-t border-gray-200 pt-4" data-test="draft-runs">
        <div class="mb-2 font-medium text-gray-900">Runs of this draft</div>
        <table class="w-full">
          <tbody class="divide-y divide-gray-100">
            <tr v-for="r in draftRuns" :key="r.id">
              <td class="py-2 pr-3 tabular-nums">#{{ r.id }}</td>
              <td class="py-2 pr-3"><HermesBadge :value="r.status" /></td>
              <td class="py-2 pr-3 text-gray-600">Engagement {{ r.unit_key }}</td>
              <td class="py-2 pr-3 text-gray-500" :title="absolute(r.created_at)">{{ relative(r.created_at) }}</td>
              <td class="py-2 text-right whitespace-nowrap">
                <button type="button" :class="SECONDARY_BUTTON" :disabled="r.status !== 'completed'" :data-test="`show-run-${r.id}`" @click="showRun(r)">Compare with live</button>
                <router-link :to="{ name: 'AdminHermesRun', params: { runId: r.id } }" :class="[TEXT_BUTTON, 'ml-3']">Details</router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </HermesCard>

    <div v-if="compareOpen && draftRun" class="fixed inset-0 z-30 flex flex-col bg-white" role="dialog" aria-modal="true" data-test="comparison" tabindex="-1" @keydown.esc="compareOpen = false">
      <div class="flex items-center gap-3 border-b border-gray-200 px-6 py-3">
        <span class="font-semibold text-gray-900">Draft “{{ draftRun.draft_name }}” vs live</span>
        <span class="text-gray-500">· engagement {{ draftRun.unit_key }}</span>
        <button type="button" :class="[SECONDARY_BUTTON, 'ml-auto']" data-test="close-comparison" @click="compareOpen = false">Close</button>
      </div>
      <div class="grid flex-1 grid-cols-2 divide-x divide-gray-200 overflow-hidden">
        <div class="overflow-y-auto bg-gray-50 p-6">
          <div class="mb-3 font-medium text-gray-900">Draft <span class="text-gray-500">· run #{{ draftRun.id }}</span></div>
          <p v-if="draftRun.status === 'failed'" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ draftRun.error || 'The draft run failed.' }}</p>
          <p v-else-if="!draftResult" class="text-gray-500">{{ draftLive ? 'Waiting for the result…' : 'No result recorded.' }}</p>
          <SummaryDocumentPage v-else :title="template.name" :sections="sectionsOf(draftRun.display)" :content="draftResult.data || {}" :patient-name="draftResult.entity_name || ''" :generated="absolute(draftResult.created_at)" />
        </div>
        <div class="overflow-y-auto bg-gray-50 p-6">
          <div class="mb-3 font-medium text-gray-900">Live <span v-if="liveRun" class="text-gray-500">· v{{ liveRun.version || '?' }} · run #{{ liveRun.id }}</span></div>
          <p v-if="!liveResult" class="text-gray-500">No live result for this engagement yet. Generate one with Summarize on the engagement, then compare again.</p>
          <SummaryDocumentPage v-else :title="template.name" :sections="sectionsOf(liveRun.display || template.display)" :content="liveResult.data || {}" :patient-name="liveResult.entity_name || ''" :generated="absolute(liveResult.created_at)" />
        </div>
      </div>
    </div>

    <div v-if="diff" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true" data-test="diff-dialog">
      <div class="w-[900px] max-h-[80vh] flex flex-col rounded-lg bg-white border border-gray-200">
        <div class="bg-primary-100 px-4 py-3 rounded-t-lg flex items-center font-semibold text-gray-900">
          {{ diff.title }}
          <button type="button" class="ml-auto font-normal text-gray-700" @click="diff = null">Close</button>
        </div>
        <div class="overflow-y-auto p-4 space-y-4">
          <p v-if="!diff.parts.length" class="text-gray-600">No differences.</p>
          <div v-for="d in diff.parts" :key="d.key">
            <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">{{ DIFF_LABELS[d.key] || d.key }}</h3>
            <pre class="whitespace-pre-wrap break-words"><span v-for="(l, k) in d.lines" :key="k" :class="['block px-2', l.op === '-' ? 'bg-gray-100 line-through text-gray-600' : (l.op === '+' ? 'bg-secondary-100 text-gray-900' : 'text-gray-700')]">{{ l.op }} {{ l.text }}</span></pre>
          </div>
        </div>
      </div>
    </div>

    <div v-if="promote" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true" data-test="promote-dialog">
      <div class="w-[640px] rounded-lg bg-white border border-gray-200">
        <div class="bg-primary-100 px-4 py-3 rounded-t-lg font-semibold text-gray-900">Replace the live template with “{{ current.name }}”?</div>
        <div class="p-4 space-y-3 text-gray-900">
          <p>This draft's prompt, result schema and display replace the live template. <strong>Every run from now on uses it</strong>: Summarize, the program buttons and schedules.</p>
          <p data-test="version-change">The template goes from <strong>v{{ promote.from_version }} → v{{ promote.to_version }}</strong>.</p>
          <p v-if="promote.live_changed" class="rounded-md bg-neutral-100 px-3 py-2" data-test="live-changed">
            Live has changed since this draft was created: the draft is based on v{{ promote.base_version }}, and live is now v{{ promote.from_version }}. Promoting replaces those changes.
          </p>
          <label class="flex items-center gap-2"><input v-model="promoteConfirmed" type="checkbox" data-test="promote-confirm"> I understand this changes what users get.</label>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" :class="TEXT_BUTTON" @click="promote = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="!promoteConfirmed || busy" data-test="promote-submit" @click="doPromote">Promote to v{{ promote.to_version }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SummaryDocumentPage from '@/components/Care_Management/Widgets/PatientEngagement/SummaryDocumentPage.vue';
import {
  createDraft, discardDraft, listDraftRuns, listDrafts, liveRunFor, promoteDraft, promotePreview, runDraft, saveDraft,
} from '@/services/api/hermesDrafts';
import HermesAuthoringAssistant from './HermesAuthoringAssistant.vue';
import HermesBadge from './HermesBadge.vue';
import HermesCard from './HermesCard.vue';
import HermesSectionBuilder from './HermesSectionBuilder.vue';
import { sectionsFrom, toTemplate } from './documentSections';
import { lintPrompt } from './promptLint';
import {
  API, INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, SIZED_INPUT, TEXT_BUTTON, absolute, diffParts, errorOf, relative, renderPrompt,
} from './hermes';

const TERMINAL = ['completed', 'failed', 'cancelled'];
const POLL_MS = 3000;
const EDIT_TABS = [{ key: 'document', label: 'Document' }, { key: 'prompt', label: 'Prompt' }, { key: 'advanced', label: 'Advanced' }];
const DIFF_KEYS = ['prompt', 'result_schema', 'display'];
const DIFF_LABELS = { prompt: 'Prompt', result_schema: 'Result schema', display: 'Display' };

function asText(value) {
  return value && Object.keys(value).length ? JSON.stringify(value, null, 2) : '';
}

export default {
  name: 'HermesTemplateDrafts',
  components: { HermesAuthoringAssistant, HermesBadge, HermesCard, HermesSectionBuilder, SummaryDocumentPage },
  props: {
    template: { type: Object, required: true },
    // The template's History (newest first), used to diff a draft against the version it was copied from.
    versions: { type: Array, default: () => [] },
  },
  emits: ['promoted'],
  data() {
    return {
      drafts: [], current: null, form: { prompt: '', schemaText: '', displayText: '' }, newName: '',
      error: '', note: '', busy: false, confirmDiscard: false, workId: null,
      draftRun: null, draftResult: null, liveRun: null, liveResult: null, timer: null,
      draftRuns: [], compareOpen: false, promote: null, promoteConfirmed: false, editTab: 'document', promptView: 'read', sections: [], diff: null,
      EDIT_TABS, DIFF_LABELS, INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, SIZED_INPUT, TEXT_BUTTON,
    };
  },
  computed: {
    parsed() {
      try {
        return {
          schema: this.form.schemaText.trim() ? JSON.parse(this.form.schemaText) : null,
          display: this.form.displayText.trim() ? JSON.parse(this.form.displayText) : {},
          error: '',
        };
      } catch (e) {
        return { error: `Not valid JSON: ${e.message}` };
      }
    },
    jsonError() {
      return this.parsed.error;
    },
    dirty() {
      if (!this.current) return false;
      return this.form.prompt !== this.current.prompt
        || this.form.schemaText !== asText(this.current.result_schema)
        || this.form.displayText !== asText(this.current.display);
    },
    isSuperuser() {
      return !!this.$store?.getters['auth/current_user']?.is_superuser;
    },
    promptNotes() {
      return lintPrompt(this.form.prompt);
    },
    promptHtml() {
      return renderPrompt(this.form.prompt);
    },
    liveMoved() {
      return !!this.current && this.current.base_version !== this.template.version;
    },
    baseVersion() {
      const v = this.current && this.versions.find((x) => x.version === this.current.base_version);
      return v ? { prompt: v.definition.prompt, result_schema: v.definition.result_schema, display: v.display || {} } : null;
    },
    draftLive() {
      return !!this.draftRun && !TERMINAL.includes(this.draftRun.status);
    },
  },
  watch: {
    sections: { deep: true, handler: 'syncFromSections' },
    // The open draft lives in the path (/templates/516/drafts/3), so back/forward and links open it.
    '$route.params.draftId': 'openFromRoute',
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    absolute, relative,
    sectionsOf(display) {
      return (display || {}).display_sections || [];
    },
    async load() {
      try {
        this.drafts = await listDrafts(this.template.id);
        this.openFromRoute();
      } catch (e) {
        this.error = errorOf(e, 'The drafts could not be loaded.');
      }
    },
    syncFromSections() {
      if (this.editTab !== 'document' || this.parsed.error) return;
      const { resultSchema, display } = toTemplate(this.sections, this.parsed.schema, this.parsed.display);
      const schemaText = asText(resultSchema);
      const displayText = asText(display);
      // Leave the JSON untouched when the sections produce the same thing, so opening a draft isn't "unsaved".
      if (JSON.stringify(resultSchema) !== JSON.stringify(this.parsed.schema)) this.form.schemaText = schemaText;
      if (JSON.stringify(display) !== JSON.stringify(this.parsed.display)) this.form.displayText = displayText;
    },
    loadSections() {
      if (this.parsed.error) return;
      this.sections = sectionsFrom(this.parsed.schema, this.parsed.display);
    },
    switchTab(key) {
      if (key === 'document' && this.editTab === 'advanced') {
        if (this.parsed.error) return;
        this.editTab = key;
        this.loadSections();
        return;
      }
      this.editTab = key;
    },
    showDiff(which) {
      const draft = { prompt: this.form.prompt, result_schema: this.parsed.schema, display: this.parsed.display };
      const live = { prompt: this.template.prompt, result_schema: this.template.result_schema, display: this.template.display || {} };
      const base = this.baseVersion;
      this.diff = which === 'mine'
        ? { title: `Your changes since v${this.current.base_version}`, parts: diffParts(base, draft, DIFF_KEYS) }
        : { title: `Changes in live, v${this.current.base_version} → v${this.template.version}`, parts: diffParts(base, live, DIFF_KEYS) };
    },
    routeDraftId() {
      return Number((this.$route && this.$route.params.draftId) || 0);
    },
    openFromRoute() {
      const wanted = this.routeDraftId();
      if (wanted === ((this.current && this.current.id) || 0)) return;
      const draft = wanted && this.drafts.find((d) => d.id === wanted);
      if (draft) this.open(draft);
      else this.close();
    },
    setRouteDraft(id) {
      if (!this.$router || this.routeDraftId() === (id || 0)) return;
      const { draftId, ...params } = this.$route.params; // eslint-disable-line no-unused-vars
      this.$router.push({ params: id ? { ...params, section: 'drafts', draftId: String(id) } : params });
    },
    close() {
      clearTimeout(this.timer);
      this.current = null;
      this.compareOpen = false;
      this.draftRun = null;
      this.draftRuns = [];
    },
    open(draft) {
      this.setRouteDraft(draft.id);
      this.current = draft;
      this.form = { prompt: draft.prompt, schemaText: asText(draft.result_schema), displayText: asText(draft.display) };
      this.editTab = 'document';
      this.promptView = 'read';
      this.loadSections();
      this.note = '';
      this.error = '';
      this.confirmDiscard = false;
      this.draftRun = null;
      this.compareOpen = false;
      this.draftRuns = [];
      this.loadDraftRuns();
    },
    async act(fn, fallback) {
      this.busy = true;
      this.error = '';
      try {
        return await fn();
      } catch (e) {
        this.error = errorOf(e, fallback);
        return null;
      } finally {
        this.busy = false;
      }
    },
    // An accepted assistant proposal replaces the form; the draft is then unsaved until the person saves it.
    applyProposal({ prompt, result_schema: resultSchema, display }) {
      this.form = { prompt, schemaText: asText(resultSchema), displayText: asText(display) };
      this.loadSections();
      this.note = 'Changes applied. Review them, then save the draft.';
    },
    async create() {
      const draft = await this.act(() => createDraft({ template: this.template.id, name: this.newName.trim() }), 'The draft could not be created.');
      if (!draft) return;
      this.newName = '';
      this.drafts = [draft, ...this.drafts];
      this.open(draft);
    },
    async save() {
      const draft = await this.act(() => saveDraft(this.current.id, {
        prompt: this.form.prompt, result_schema: this.parsed.schema, display: this.parsed.display,
      }), 'The draft could not be saved.');
      if (!draft) return;
      this.drafts = this.drafts.map((d) => (d.id === draft.id ? draft : d));
      this.open(draft);
      this.note = 'Draft saved. Live is unchanged.';
    },
    async discard() {
      const id = this.current.id;
      const ok = await this.act(async () => { await discardDraft(id); return true; }, 'The draft could not be discarded.');
      if (!ok) return;
      this.drafts = this.drafts.filter((d) => d.id !== id);
      this.close();
      this.setRouteDraft(null);
    },
    setWorkId(event) {
      const digits = event.target.value.replace(/\D/g, '');
      event.target.value = digits;
      this.workId = digits ? Number(digits) : null;
    },
    async run() {
      const started = await this.act(() => runDraft(this.current.id, { work_id: this.workId }), 'The draft could not be run.');
      if (!started) return;
      this.draftRun = started;
      this.draftResult = null;
      this.loadDraftRuns();
      this.poll();
    },
    async poll() {
      clearTimeout(this.timer);
      try {
        this.draftRun = (await this.$axios.get(`${API}/runs/${this.draftRun.id}/`)).data;
        if (!this.draftLive) {
          this.draftResult = await this.firstResult(this.draftRun.id);
          this.loadDraftRuns();
          return;
        }
      } catch (e) {
        this.error = errorOf(e, 'The draft run could not be loaded.');
        return;
      }
      this.timer = setTimeout(() => this.poll(), POLL_MS);
    },
    async loadDraftRuns() {
      if (!this.current) return;
      try {
        this.draftRuns = await listDraftRuns(this.current.id);
      } catch {
        this.draftRuns = [];
      }
    },
    async showRun(run) {
      clearTimeout(this.timer);
      this.compareOpen = true;
      this.draftRun = run;
      this.$nextTick(() => {
        const overlay = this.$el.querySelector && this.$el.querySelector('[data-test="comparison"]');
        if (overlay) overlay.focus();
      });
      this.draftResult = null;
      this.workId = Number(run.unit_key) || null;
      await this.loadLive(run.id);
      if (this.draftLive) this.poll();
      else this.draftResult = await this.firstResult(run.id);
    },
    async firstResult(runId) {
      const { data } = await this.$axios.get(`${API}/runs/${runId}/results/`);
      return (data.results || [])[0] || null;
    },
    async loadLive(draftRunId) {
      this.liveRun = null;
      this.liveResult = null;
      const live = await liveRunFor(draftRunId);
      if (!live) return;
      this.liveRun = live;
      this.liveResult = await this.firstResult(live.id);
    },
    async openPromote() {
      const preview = await this.act(() => promotePreview(this.current.id), 'The promotion could not be prepared.');
      if (!preview) return;
      this.promoteConfirmed = false;
      this.promote = preview;
    },
    async doPromote() {
      const template = await this.act(() => promoteDraft(this.current.id, {
        confirm: true, expected_version: this.promote.from_version,
      }), 'The draft could not be promoted.');
      this.promote = null;
      if (!template) return;
      this.drafts = this.drafts.filter((d) => d.id !== this.current.id);
      this.close();
      this.setRouteDraft(null);
      this.$emit('promoted', template);
    },
  },
};
</script>
